import React, {useState, useCallback} from 'react';
import {useMutation} from '@tanstack/react-query';
import {AdminService} from '@/services/adminService';
import Card from '@/components/common/Card';
import {Loader2} from 'lucide-react';

interface Props {
    className?: string;
}

interface PhotoSearchState {
    photoEmail: string;
}

interface PhotoResults {
    frontInsurance: string;
    backInsurance: string;
    frontId: string;
}

interface Toast {
    error: boolean;
    message: string;
}

/**
 * PhotoSearch component - Admin page to search for user insurance photos
 * Refactored to follow docs/SUMMARY.md best practices
 */
export const PhotoSearch: React.FC<Props> = () => {
    const [searchState, setSearchState] = useState<PhotoSearchState>({
        photoEmail: '',
    });

    const [photos, setPhotos] = useState<PhotoResults>({
        frontInsurance: '',
        backInsurance: '',
        frontId: '',
    });

    const [searchedPhotos, setSearchedPhotos] = useState(false);
    const [toast, setToast] = useState<Toast>({
        error: false,
        message: '',
    });

    // React Query mutation for photo search
    const searchMutation = useMutation({
        mutationFn: (email: string) => AdminService.searchPhotos(email),
        onSuccess: (data) => {
            // Process the photo data - matching old component structure
            const newPhotos = {
                frontInsurance: '',
                backInsurance: '',
                frontId: '',
            };

            // Check if API response has success field like old version
            if (data && data.success) {
                if (data.frontInsurance) {
                    newPhotos.frontInsurance = data.frontInsurance;
                }
                if (data.backInsurance) {
                    newPhotos.backInsurance = data.backInsurance;
                }
                if (data.frontId) {
                    newPhotos.frontId = data.frontId;
                }
            } else {
                // Fallback to direct structure if no success field
                newPhotos.frontInsurance = data.frontInsurance || '';
                newPhotos.backInsurance = data.backInsurance || '';
                newPhotos.frontId = data.frontId || '';
            }

            setPhotos(newPhotos);
            setSearchedPhotos(true);

            const hasPhotos = Object.values(newPhotos).some((photo) => photo);
            if (!hasPhotos) {
                setToast({
                    error: false,
                    message: 'No photos found for this email.',
                });
            } else {
                setToast({error: false, message: ''});
            }
        },
        onError: (error) => {
            console.error('Photo search error:', error);
            setToast({
                error: true,
                message: 'Error searching photos. Please try again.',
            });
            setSearchedPhotos(false);
        },
    });

    const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchState({
            photoEmail: event.target.value,
        });
    }, []);

    const handleSubmit = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault();
            setToast({error: false, message: ''});
            setSearchedPhotos(false);

            if (!searchState.photoEmail.trim()) {
                setToast({
                    error: true,
                    message: 'Please enter an email address.',
                });
                return;
            }

            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(searchState.photoEmail)) {
                setToast({
                    error: true,
                    message: 'Please enter a valid email address.',
                });
                return;
            }

            searchMutation.mutate(searchState.photoEmail);
        },
        [searchState.photoEmail, searchMutation]
    );

    const openPhotoModal = useCallback((photoUrl: string) => {
        if (photoUrl) {
            window.open(photoUrl, '_blank', 'width=800,height=600');
        }
    }, []);

    return (
        <div className="space-y-6">
            <Card>
                <Card.Header>
                    <Card.Title>Search for user insurance photos</Card.Title>
                </Card.Header>
                <Card.Content>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="photoEmail" className="block text-sm font-medium text-gray-700 mb-1">
                                Email Address
                            </label>
                            <input
                                id="photoEmail"
                                type="email"
                                value={searchState.photoEmail}
                                onChange={handleInputChange}
                                placeholder="Enter user email address"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                required
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <button
                                type="submit"
                                disabled={searchMutation.isPending}
                                className="flex items-center space-x-2 px-4 py-2 bg-mld-primary text-white rounded-md hover:bg-mld-primary/90 focus:outline-none focus:ring-2 focus:ring-mld-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed">
                                {searchMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                                <span>{searchMutation.isPending ? 'Searching...' : 'Search Photos'}</span>
                            </button>

                            {toast.message && (
                                <div
                                    className="px-3 py-2 rounded-md text-sm text-white"
                                    style={{backgroundColor: toast.error ? '#F15E87' : '#42D4B0'}}>
                                    {toast.message}
                                </div>
                            )}
                        </div>
                    </form>
                </Card.Content>
            </Card>

            {searchedPhotos && (
                <Card>
                    <Card.Header>
                        <Card.Title>Insurance Photos for {searchState.photoEmail}</Card.Title>
                    </Card.Header>
                    <Card.Content>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Front Insurance */}
                            <div className="space-y-2">
                                <h3 className="text-sm font-medium text-gray-700">Front Insurance</h3>
                                {photos.frontInsurance ? (
                                    <div className="relative group">
                                        <img
                                            src={photos.frontInsurance}
                                            alt="Front Insurance"
                                            className="w-full h-48 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                            onClick={() => openPhotoModal(photos.frontInsurance)}
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-50 rounded-lg">
                                            <span className="text-white text-sm font-medium">Click to enlarge</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="w-full h-48 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200">
                                        <span className="text-gray-500 text-sm">No photo available</span>
                                    </div>
                                )}
                            </div>

                            {/* Back Insurance */}
                            <div className="space-y-2">
                                <h3 className="text-sm font-medium text-gray-700">Back Insurance</h3>
                                {photos.backInsurance ? (
                                    <div className="relative group">
                                        <img
                                            src={photos.backInsurance}
                                            alt="Back Insurance"
                                            className="w-full h-48 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                            onClick={() => openPhotoModal(photos.backInsurance)}
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-50 rounded-lg">
                                            <span className="text-white text-sm font-medium">Click to enlarge</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="w-full h-48 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200">
                                        <span className="text-gray-500 text-sm">No photo available</span>
                                    </div>
                                )}
                            </div>

                            {/* Front ID */}
                            <div className="space-y-2">
                                <h3 className="text-sm font-medium text-gray-700">Front ID</h3>
                                {photos.frontId ? (
                                    <div className="relative group">
                                        <img
                                            src={photos.frontId}
                                            alt="Front ID"
                                            className="w-full h-48 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                            onClick={() => openPhotoModal(photos.frontId)}
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-50 rounded-lg">
                                            <span className="text-white text-sm font-medium">Click to enlarge</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="w-full h-48 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200">
                                        <span className="text-gray-500 text-sm">No photo available</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {!Object.values(photos).some((photo) => photo) && (
                            <div className="text-center py-8">
                                <p className="text-gray-500">No insurance photos found for this user.</p>
                            </div>
                        )}
                    </Card.Content>
                </Card>
            )}
        </div>
    );
};

export default PhotoSearch;
